import { Phone, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EditableText } from "@/shared/components/editable/EditableText";
import type { CTAContent } from "../types/landing.types";
import type { EditableContent } from "@/shared/types/editable.types";
import { useAuthContext } from "@/features/auth/hooks/useAuthContext";
import { useAnalytics } from "@/features/analytics/hooks/useAnalytics";
import { buildWhatsAppUrl, extractWhatsAppNumber } from "@/shared/utils/whatsapp";

interface CTASectionProps {
    content: CTAContent;
    onSave: (content: EditableContent) => Promise<void>;
}

export const WhatsAppIcon = ({ className }: { className?: string }) => (
    <svg
        viewBox="0 0 256 259"
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="xMidYMid"
        className={className}
    >
        <path
            d="m67.663 221.823 4.185 2.093c17.44 10.463 36.971 15.346 56.503 15.346 61.385 0 111.609-50.224 111.609-111.609 0-29.297-11.859-57.897-32.785-78.824-20.927-20.927-48.83-32.785-78.824-32.785-61.385 0-111.61 50.224-110.912 112.307 0 20.926 6.278 41.156 16.741 58.594l2.79 4.186-11.16 41.156 41.853-10.464Z"
            fill="#00E676"
        />
        <path
            d="M219.033 37.668C195.316 13.254 162.531 0 129.048 0 57.898 0 .698 57.897 1.395 128.35c0 22.322 6.278 43.947 16.742 63.478L0 258.096l67.663-17.439c18.834 10.464 39.76 15.347 60.688 15.347 70.453 0 127.653-57.898 127.653-128.35 0-34.181-13.254-66.269-36.97-89.986ZM129.048 234.38c-18.834 0-37.668-4.882-53.712-14.648l-4.185-2.093-40.458 10.463 10.463-39.76-2.79-4.186C7.673 134.63 22.322 69.058 72.546 38.365c50.224-30.692 115.097-16.043 145.79 34.181 30.692 50.224 16.043 115.097-34.18 145.79-16.045 10.463-35.576 16.043-55.108 16.043Zm61.385-77.428-7.673-3.488s-11.16-4.883-18.136-8.371c-.698 0-1.395-.698-2.093-.698-2.093 0-3.488.698-4.883 1.396 0 0-.697.697-10.463 11.858-.698 1.395-2.093 2.093-3.488 2.093h-.698c-.697 0-2.092-.698-2.79-1.395l-3.488-1.395c-7.673-3.488-14.648-7.674-20.229-13.254-1.395-1.395-3.488-2.79-4.883-4.185-4.883-4.883-9.766-10.464-13.253-16.742l-.698-1.395c-.697-.698-.697-1.395-1.395-2.79 0-1.395 0-2.79.698-3.488 0 0 2.79-3.488 4.882-5.58 1.396-1.396 2.093-3.488 3.488-4.883 1.395-2.093 2.093-4.883 1.395-6.976-.697-3.488-9.068-22.322-11.16-26.507-1.396-2.093-2.79-2.79-4.883-3.488H83.01c-1.396 0-2.79.698-4.186.698l-.698.697c-1.395.698-2.79 2.093-4.185 2.79-1.395 1.396-2.093 2.79-3.488 4.186-4.883 6.278-7.673 13.951-7.673 21.624 0 5.58 1.395 11.161 3.488 16.044l.698 2.093c6.278 13.253 14.648 25.112 25.81 35.575l2.79 2.79c2.092 2.093 4.185 3.488 5.58 5.58 14.649 12.557 31.39 21.625 50.224 26.508 2.093.697 4.883.697 6.976 1.395h6.975c3.488 0 7.673-1.395 10.464-2.79 2.092-1.395 3.487-1.395 4.882-2.79l1.396-1.396c1.395-1.395 2.79-2.092 4.185-3.487 1.395-1.395 2.79-2.79 3.488-4.186 1.395-2.79 2.092-6.278 2.79-9.765v-4.883s-.698-.698-2.093-1.395Z"
            fill="#FFF"
        />
    </svg>
);

export function CTASection({ content, onSave }: CTASectionProps) {
    const { isAdmin } = useAuthContext();
    const { trackEvent } = useAnalytics();
    const handleWhatsAppClick = () => {
        trackEvent("cta_whatsapp_click", { location: "cta_section" });
        const url = buildWhatsAppUrl(content.whatsappNumber);
        if (url) {
            window.open(url, "_blank");
        }
    };

    return (
        <div className="bs-landing-section">
            <div className="flex flex-col items-center w-full">
                <div className="text-center mb-3 bs-content-1">
                    <div className="flex flex-col md:flex-row justify-center items-center gap-3 px-3 md:px-4">
                        {!isAdmin && (
                            <Button
                                onClick={handleWhatsAppClick}
                                className="btn btn-lg btn-primary bg-valora-primary hover:bg-valora-secondary text-white flex items-center justify-center gap-2"
                                style={{
                                    minHeight: "48px",
                                    padding: "12px 18px",
                                    fontSize: "12px",
                                    width: "100%",
                                    maxWidth: "200px",
                                }}
                            >
                                <WhatsAppIcon className="w-5 h-5 shrink-0" />
                                <span className="truncate">
                                    <EditableText
                                        content={{
                                            value: content.text,
                                            id: "description",
                                            type: "text",
                                            section: "cta",
                                        }}
                                        onSave={onSave}
                                        as="span"
                                        className="font-semibold"
                                    />
                                </span>
                            </Button>
                        )}

                        {isAdmin && (
                            <div className="w-full bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-6 flex flex-col items-center gap-8 transition-[border-color,box-shadow]">
                                <div className="w-full flex flex-col md:flex-row items-center justify-around gap-8">
                                    <div className="flex flex-col items-center gap-3 w-full max-w-60">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                            Previsualización
                                        </span>
                                        <div className="relative flex items-center justify-center gap-3 w-full min-h-13 px-6 bg-valora-primary rounded-xl shadow-lg shadow-blue-200 transition-[box-shadow,background-color]">
                                            <WhatsAppIcon className="w-5 h-5 shrink-0 drop-shadow-sm" />
                                            <div className="flex-1 relative text-white focus-within:text-gray-900">
                                                <EditableText
                                                    content={{
                                                        value: content.text,
                                                        id: "text",
                                                        type: "text",
                                                        section: "cta",
                                                    }}
                                                    onSave={onSave}
                                                    as="p"
                                                    className="font-bold text-xs tracking-wide cursor-text text-inherit"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hidden md:block w-px h-16 bg-gray-200" />

                                    <div className="flex flex-col gap-3 w-full max-w-70">
                                        <div className="flex items-center gap-2">
                                            <div className="p-1.5 bg-green-100 rounded-md">
                                                <Phone className="w-4 h-4 text-green-600" />
                                            </div>
                                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                                Link de WhatsApp
                                            </span>
                                        </div>
                                        <div className="bg-white border border-gray-300 rounded-lg px-4 py-3 shadow-sm focus-within:ring-2 focus-within:ring-[#2FA4FF] transition-[box-shadow,border-color] overflow-visible">
                                            <EditableText
                                                content={{
                                                    value: extractWhatsAppNumber(content.whatsappNumber),
                                                    id: "whatsappNumber",
                                                    type: "text",
                                                    section: "cta",
                                                }}
                                                onSave={onSave}
                                                as="p"
                                                singleLine={true}
                                                className="mb-0 text-gray-700 font-medium text-sm outline-none text-center"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-gray-100 w-full flex justify-center">
                                    <Button
                                        type="button"
                                        variant="link"
                                        onClick={handleWhatsAppClick}
                                        className="text-valora-primary hover:text-valora-secondary  text-xs font-semibold underline underline-offset-4 decoration-2 h-auto p-0 gap-1 cursor-pointer"
                                    >
                                        <ExternalLink className="w-3 h-3" />
                                        Link WhatsApp
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
